import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ActionHelper,
  OnyxDatePipe,
  OnyxDotComponent,
  OnyxLinkComponent,
  OnyxToastService,
} from '@onyx/angular';
import { catchError, EMPTY, exhaustMap, finalize, Subject, tap } from 'rxjs';
import { AuthStore } from '../../../../../../auth/common/stores/auth.store';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { EmployeeStatus } from '../../enums/employee-status';
import type { Employee } from '../../interfaces/employee';
import { EmployeesService } from '../../services/employees.service';

@Component({
  selector: 'app-employee-status',
  imports: [OnyxDotComponent, OnyxLinkComponent, OnyxDatePipe, TranslatePipe],
  templateUrl: './employee-status.component.html',
  styleUrl: './employee-status.component.scss',
})
export class EmployeeStatusComponent implements OnInit {
  private readonly authStore = inject(AuthStore);
  private readonly employeesService = inject(EmployeesService);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly employee = input.required<Employee>();
  public readonly displayStatus = input(false);

  protected readonly EmployeeStatus = EmployeeStatus;
  protected readonly DictionaryCode = DictionaryCode;

  protected resendInviteLoading = signal(false);
  protected copyInviteLoading = signal(false);

  protected resendInvite$ = new Subject<void>();
  protected copyInviteLink$ = new Subject<void>();

  protected hasAdd = computed(() =>
    this.authStore.hasPermission('employees.add'),
  );

  protected hasCopyInviteLink = computed(() =>
    this.authStore.hasPermission('employees.copyInviteLink'),
  );

  public ngOnInit(): void {
    this.resendInvite$
      .pipe(
        tap(() => this.resendInviteLoading.set(true)),
        exhaustMap(() =>
          this.employeesService.reinviteEmployee(this.employee().uuid).pipe(
            catchError(() => EMPTY),
            finalize(() => this.resendInviteLoading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() =>
        this.toastService.showSuccess({
          message: 'employees.toasts.invitationSentAgain',
        }),
      );

    this.copyInviteLink$
      .pipe(
        tap(() => this.copyInviteLoading.set(true)),
        exhaustMap(() =>
          this.employeesService.getInviteLink(this.employee().uuid).pipe(
            catchError(() => EMPTY),
            finalize(() => this.copyInviteLoading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((link) => ActionHelper.copy(link, this.toastService));
  }
}
